<template>
    <div class="table-container w-full">
        <div class="mt-1 flex justify-between lg:flex-row flex-col">
            <div class="lg:w-1/3 w-full mr-2 mb-2 lg:mb-0">
                <a-input-search
                    data-cy="audit-logs-input-search"
                    @search="onSearch"
                    :placeholder="$t('globals.tables.search')"
                />
            </div>
            <div
                class="lg:w-1/2 xl:w-2/5 w-full mr-2 mb-2 lg:mb-0"
                v-if="showCompanyFilter"
                data-cy="audit-logs-filter"
            >
                <a-select
                    show-search
                    :placeholder="$t('globals.tables.filter')"
                    style="width: 100%"
                    option-filter-prop="children"
                    :filter-option="filterOption"
                    @search="handleFilterSearch"
                    @change="handleCompanyFilterChange"
                    :allowClear="true"
                >
                    <a-select-option v-for="(value, index) in company_list" :key="index" :value="index">
                        {{ value.company_name }}
                    </a-select-option>
                </a-select>
            </div>
            <div class="lg:w-1/2 xl:w-2/5 w-full mr-2 mb-2 lg:mb-0" v-if="showFilter" data-cy="audit-logs-filter">
                <a-select
                    show-search
                    :placeholder="$t('globals.tables.filter')"
                    style="width: 100%"
                    :filter-option="false"
                    :not-found-content="null"
                    @search="handleFilterSearch"
                    @change="handleFilterChange"
                    :allowClear="true"
                >
                    <a-select-opt-group
                        v-if="
                            company_list.some(
                                (value) =>
                                    value.association_code?.toLowerCase().includes(filter_value.toLowerCase()) ||
                                    value.company_name?.toLowerCase().includes(filter_value.toLowerCase()),
                            )
                        "
                        label="Company"
                    >
                        <template v-for="(value, index) in company_list">
                            <a-select-option
                                v-if="
                                    value.association_code?.toLowerCase().includes(filter_value.toLowerCase()) ||
                                    value.company_name?.toLowerCase().includes(filter_value.toLowerCase())
                                "
                                :key="`${index}-company`"
                                :value="`${index}-company`"
                            >
                                {{ value.association_code }} - {{ value.company_name }}
                            </a-select-option>
                        </template>
                    </a-select-opt-group>
                    <a-select-opt-group
                        v-if="
                            association_list.some((value) =>
                                value.name?.toLowerCase().includes(filter_value.toLowerCase()),
                            )
                        "
                        label="Association"
                    >
                        <template v-for="(value, index) in association_list">
                            <a-select-option
                                v-if="value.name?.toLowerCase().includes(filter_value.toLowerCase())"
                                :key="`${index}-association`"
                                :value="`${index}-association`"
                            >
                                {{ value.name }}
                            </a-select-option>
                        </template>
                    </a-select-opt-group>
                </a-select>
            </div>
            <div class="w-auto mb-2 lg:mb-0 flex lg:flex-row flex-col" data-cy="created-date-field">
                <ui-date-range-picker-double v-model="created_date_range" @changeDates="handleDateFilterChange" />
                <div v-if="recordsSelected && showMassActions" class="flex justify-end">
                    <a-dropdown :trigger="['click']" class="ml-4 lg:mt-0 mt-2">
                        <a-button @click="(e) => e.preventDefault()" data-cy="btn-mass-selection">
                            {{ $t('globals.tables.audit_logs.mass_actions') }}
                            <DownOutlined />
                        </a-button>
                        <template #overlay>
                            <a-menu @click="onActionButtons">
                                <a-menu-item key="export" data-cy="actions-close-item">
                                    {{ $t('globals.tables.audit_logs.actions.export') }}
                                </a-menu-item>
                            </a-menu>
                        </template>
                    </a-dropdown>
                </div>
            </div>
        </div>
        <div class="list-table pt-2 md:pt-4 overflow-x-auto overflow-visible mt-1">
            <div class="w-full">
                <a-table
                    data-cy="audit-log-table"
                    :columns="getColumns"
                    :dataSource="data"
                    :rowKey="(record) => record.id"
                    :pagination="pagination"
                    :locale="locale"
                    :loading="loading"
                    :rowSelection="rowSelection"
                    @change="handleTableChange"
                    size="middle"
                    class="ant-table-striped"
                >
                    <template #bodyCell="{ column, record, text }">
                        <template v-if="column.key === 'search_count'">
                            <span v-if="record.claim_search_count == 0 && record.cover_search_count == 0">
                                {{ record.claim_search_count }} / {{ record.cover_search_count }}
                            </span>
                            <span v-else>
                                <span
                                    @click="showSearchList(record.id, 'claim')"
                                    class="underline cursor-pointer"
                                    :class="{
                                        'text-default-purple': !isEpicvaultEnabled,
                                        'text-default-blue': isEpicvaultEnabled,
                                    }"
                                >
                                    {{ record.claim_search_count }}</span
                                >
                                /
                                <span
                                    @click="showSearchList(record.id, 'cover')"
                                    class="underline cursor-pointer"
                                    :class="{
                                        'text-default-purple': !isEpicvaultEnabled,
                                        'text-default-blue': isEpicvaultEnabled,
                                    }"
                                >
                                    {{ record.cover_search_count }}
                                </span>
                            </span>
                        </template>
                        <template v-else-if="column.key === 'created_at'">{{ displayDateTime(text) }}</template>
                        <template v-else>
                            {{ text }}
                        </template>
                    </template>
                </a-table>
                <span v-if="data.length > 0" data-cy="page-item-range" class="page-item-range flex">
                    {{
                        $t('globals.tables.page_range', {
                            pi_start: pageItemStart,
                            pi_end: pageItemEnd,
                            total: pagination.total,
                        })
                    }}
                </span>
            </div>
        </div>

        <a-modal
            :class="'epic-' + moduleStateLink"
            data-cy="audit-log-modal"
            :title="$t('user.audit_logs.search_modal_title', { type: search_type })"
            :destroyOnClose="true"
            v-model:open="modal_visible"
            :footer="null"
            width="auto"
            :dialog-style="{ padding: '0 20px', maxWidth: '1536px' }"
        >
            <div class="form-container">
                <audit-log-claims-search-table :audit_id="audit_id" v-if="search_type == 'claim'" />
                <audit-log-covers-search-table :audit_id="audit_id" v-else />
            </div>
        </a-modal>
    </div>
</template>

<script>
import AuditLogClaimsSearchTable from '@/components/tables/AuditLogClaimsSearch.vue';
import AuditLogCoversSearchTable from '@/components/tables/AuditLogCoversSearch.vue';

import UiDateRangePickerDouble from '@/components/ui/DateRangePickerDouble.vue';

import FileManager from '@/utils/file.js';
import { DownOutlined } from '@ant-design/icons-vue';

export default {
    components: {
        AuditLogClaimsSearchTable,
        AuditLogCoversSearchTable,
        UiDateRangePickerDouble,
        DownOutlined,
    },
    data() {
        return {
            data: [],
            search_value: null,
            created_date_range: {
                start_date: null,
                end_date: null,
            },
            pagination: {
                pageSize: 10,
                showSizeChanger: false,
                total: 0,
            },
            locale: {
                emptyText: this.$t('globals.tables.no_data'),
            },
            loading: false,
            processing: false,
            current_filter: {},
            sorter: {},
            params: {},
            filter_value: '',
            company_list: [],
            association_list: [],
            audit_id: '',
            search_type: '',
            modal_visible: false,
            selected_row_keys: [],
            select_all_logs: false,
        };
    },
    props: {
        company_id: {
            type: String,
            default: null,
        },
        user_id: {
            type: String,
            default: null,
        },
        association_id: {
            type: String,
            default: null,
        },
        is_admin_section: {
            type: Boolean,
            default: false,
        },
        is_association_section: {
            type: Boolean,
            default: false,
        },
    },
    mounted() {
        this.fetch();
        if (this.fetch_all) {
            this.$http
                .get('/api/admin/list-companies-by-associations')
                .then((response) => {
                    this.company_list = response.data.data;
                })
                .catch((error) => {
                    this.$notification.error({
                        message: this.$t('globals.global.notifications.error.any', { code: 'CVALCL01' }),
                    });
                });
            this.$http
                .get('/api/admin/list-associations')
                .then((response) => {
                    this.association_list = response.data.data;
                })
                .catch((error) => {
                    console.log(error);
                });
        }
        if (this.fetch_company_list) {
            this.$http
                .get('/api/admin/list-companies', {
                    params: {
                        association_id: this.$store.getters['user/currentAssociationId'],
                    },
                })
                .then((response) => {
                    this.company_list = response.data.data;
                })
                .catch((error) => {
                    this.$notification.error({
                        message: this.$t('globals.global.notifications.error.any', { code: 'CVALCL02' }),
                    });
                });
        }
    },
    computed: {
        getColumns() {
            return [
                {
                    title: this.$t('globals.tables.audit_logs.headers.company_code'),
                    key: 'company_code',
                    dataIndex: 'company_code',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.audit_logs.headers.ip_address'),
                    key: 'ip_address',
                    dataIndex: 'ip_address',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.audit_logs.headers.action'),
                    key: 'action',
                    dataIndex: 'action',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.audit_logs.headers.activity'),
                    key: 'activity',
                    dataIndex: 'activity',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.audit_logs.headers.search_count'),
                    key: 'search_count',
                    dataIndex: 'search_count',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.audit_logs.headers.accessed_by'),
                    key: 'name',
                    dataIndex: 'name',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.audit_logs.headers.created_at'),
                    key: 'created_at',
                    dataIndex: 'created_at',
                    sorter: true,
                },
            ];
        },
        pageItemStart() {
            let current_page = this.pagination.current;

            if (current_page === undefined) current_page = 1;

            return (current_page - 1) * this.pagination.pageSize + 1;
        },
        pageItemEnd() {
            let current_page = this.pagination.current,
                page_item_end = 0;

            if (current_page === undefined) current_page = 1;

            page_item_end = current_page * this.pagination.pageSize;

            if (this.pagination.total !== undefined && this.pagination.total < page_item_end) {
                return this.pagination.total;
            } else {
                return page_item_end;
            }
        },
        showFilter() {
            if (this.is_admin_section) {
                this.fetch_all = true;
                return true;
            } else {
                return false;
            }
        },
        showMassActions() {
            if (
                this.is_admin_section ||
                this.is_association_section ||
                (this.company_id != '' && this.company_id != null)
            ) {
                return true;
            } else {
                return false;
            }
        },
        showCompanyFilter() {
            if (this.association_id !== '') {
                this.fetch_company_list = true;

                return true;
            } else {
                return false;
            }
        },
        isEpicvaultEnabled() {
            return this.$store.state.system.is_epicvault_enabled ? true : false;
        },
        moduleStateLink() {
            return this.isEpicvaultEnabled ? 'default' : 'claim';
        },
        recordsSelected() {
            return this.selected_row_keys.length > 0;
        },
        rowSelection() {
            if (
                this.is_admin_section ||
                this.is_association_section ||
                (this.company_id != '' && this.company_id != null)
            ) {
                return {
                    selectedRowKeys: this.selected_row_keys,
                    onChange: this.onSelectChange,
                    selections: this.selections,
                };
            } else {
                return null;
            }
        },
        selections() {
            return [
                {
                    key: 'all-data',
                    text: this.select_all_logs ? 'Unselect All Audit Logs' : 'Select All Audit Logs',
                    onSelect: (changableRowKeys) => {
                        this.select_all_logs = !this.select_all_logs;
                        let updated_selected_rows = [];
                        if (this.select_all_logs) {
                            if (this.data.length != 0) {
                                this.data.forEach((record) => {
                                    updated_selected_rows.push(record.id);
                                });
                            }
                            this.selected_row_keys = updated_selected_rows;
                            this.$emit('onSelectChange', updated_selected_rows);
                        } else {
                            this.selected_row_keys = [];
                            this.$emit('onSelectChange', []);
                        }
                    },
                },
            ];
        },
    },
    methods: {
        fetch(params = {}) {
            this.loading = true;
            let start_date = null;
            let end_date = null;

            if (params.created_date_range !== undefined) {
                start_date = params.created_date_range.start_date
                    ? params.created_date_range.start_date.format('YYYY-MM-DD')
                    : null;
                end_date = params.created_date_range.end_date
                    ? params.created_date_range.end_date.format('YYYY-MM-DD')
                    : null;

                delete params.created_date_range;
            }

            if (this.is_admin_section) {
                params = {
                    user_id: this.user_id,
                    fetch_all: this.fetch_all,
                    start_date: start_date,
                    end_date: end_date,
                    ...params,
                };
            } else if (this.is_association_section) {
                params = {
                    association_id: this.association_id,
                    user_id: this.user_id,
                    fetch_all: null,
                    start_date: start_date,
                    end_date: end_date,
                    ...params,
                };
            } else {
                params = {
                    company_id: this.company_id,
                    user_id: this.user_id,
                    fetch_all: null,
                    start_date: start_date,
                    end_date: end_date,
                    ...params,
                };
            }

            this.params = params;

            this.$http
                .get('/api/audit-logs', {
                    params: params,
                })
                .then((response) => {
                    const pagination = { ...this.pagination };

                    // Read total count from server
                    pagination.total = response.data.meta.total_count;

                    this.loading = false;
                    this.data = response.data.data;
                    this.pagination = pagination;

                    if (this.select_all_logs) {
                        this.data.forEach((record) => {
                            this.selected_row_keys.push(record.id);
                        });
                    }
                })
                .catch((error) => {
                    if (error.response.status == 403) {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.403_mod'),
                        });
                    } else {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.any', { code: 'CVALF01' }),
                        });
                    }
                });
        },
        displayDateTime(datetime) {
            return this.$dayjs(datetime).format('YYYY-MM-DD HH:mm:ss');
        },
        handleTableChange(pagination, filters, sorter) {
            const pager = { ...this.pagination };

            pager.current = pagination.current;

            this.pagination = pager;
            this.current_filter = filters;
            this.sorter = sorter;

            this.fetch({
                ...this.params,
                results: pagination.pageSize,
                page: pagination.current,
                sortField: sorter.field,
                sortOrder: sorter.order,
                ...filters,
            });
        },
        onSearch(input) {
            let regex = /^[a-zA-Z0-9\s\(\)_-]+$/;

            if (regex.test(input) || input === '') {
                if (input === '') {
                    this.search_value = null;
                } else {
                    this.search_value = input;
                }

                this.select_all_logs = false;
                this.onSelectChange([]);

                this.fetch({
                    search_value: this.search_value,
                    company_id: this.params.company_id,
                    association_id: this.params.association_id,
                    created_date_range: this.created_date_range,
                });
            } else {
                this.$notification.error({
                    message: this.$t('globals.tables.invalid_characters'),
                });
            }
        },
        handleFilterSearch(value) {
            this.filter_value = value;
        },
        handleFilterChange(value) {
            let company_id = null;
            let association_id = null;
            let fetch_all = null;

            if (value == undefined) {
                fetch_all = true;
            } else {
                if (value.includes('company')) {
                    company_id = this.company_list[parseInt(value)].company_id;
                    association_id = this.company_list[parseInt(value)].association_id;
                } else {
                    association_id = this.association_list[parseInt(value)].id;
                }
            }

            this.select_all_logs = false;
            this.onSelectChange([]);

            this.fetch({
                fetch_all: fetch_all,
                search_value: this.search_value,
                company_id: company_id,
                association_id: association_id,
                created_date_range: this.params.created_date_range,
            });

            this.filter_value = '';
            this.pagination.current = 1;
        },
        handleCompanyFilterChange(value) {
            let company_id = null;
            if (value == undefined) {
                this.fetch_company_list = true;
            } else {
                this.fetch_company_list = null;
                company_id = this.company_list[value].company_id;
            }

            this.select_all_logs = false;
            this.onSelectChange([]);

            this.fetch({
                fetch_all: null,
                search_value: this.search_value,
                company_id: company_id,
                association_id: this.association_id,
                created_date_range: this.params.created_date_range,
            });

            this.pagination.current = 1;
        },
        handleDateFilterChange() {
            this.select_all_logs = false;
            this.onSelectChange([]);

            this.fetch({
                fetch_all: this.params.fetch_all ? this.params.fetch_all : null,
                search_value: this.params.search_value,
                company_id: this.params.company_id,
                association_id: this.params.association_id,
                created_date_range: this.created_date_range,
            });

            this.pagination.current = 1;
        },
        filterOption(input, option) {
            return option.children()[0].children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
        },
        showSearchList(audit_id, type) {
            this.audit_id = audit_id;
            this.search_type = type;
            this.modal_visible = true;
        },
        handleModalCancel(event) {
            this.modal_visible = false;
        },
        reload() {
            const filters = this.current_filter;
            this.select_all_logs = false;
            this.onSelectChange([]);

            this.fetch({
                ...this.params,
                results: this.pagination.pageSize,
                page: this.pagination.current,
                sortField: this.sorter.field,
                sortOrder: this.sorter.order,
                ...filters,
            });
        },
        onExport() {
            let params = {};

            if (this.is_association_section) {
                params = {
                    association_id: this.association_id,
                };
            } else if (!this.is_admin_section) {
                params = {
                    company_id: this.company_id,
                };
            }

            if (this.select_all_logs) {
                params.fetch_all = true;
            } else {
                params.ids = this.selected_row_keys;
            }

            this.$http
                .post('/api/audit-logs/export', params)
                .then((response) => {
                    if (
                        _.includes(
                            [
                                'text/csv; charset=UTF-8',
                                'text/csv; charset=utf-8',
                                'text/plain; charset=UTF-8',
                                'text/plain; charset=utf-8',
                            ],
                            response.headers['content-type'],
                        )
                    ) {
                        this.$notification.success({
                            message: this.$t('admin.list_manager.notifications.success.export'),
                            message: this.$t('user.audit_logs.notifications.success.export'),
                        });

                        FileManager.download(response);
                    } else {
                        this.$notification.error({ message: response.data.message });
                    }
                })
                .catch((error) => {
                    if (error.response.status == 422) {
                        this.validation = error.response.data;
                    } else if (error.response.status == 403) {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.403_mod'),
                        });
                    } else {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.any', { code: 'CVALF02' }),
                        });
                    }
                });
        },
        onSelectChange(selected_row_keys, selected_rows) {
            if (this.select_all_logs) {
                this.select_all_logs = false;
                this.selected_row_keys = [];
                this.$emit('onSelectChange', []);
            } else {
                this.selected_row_keys = selected_row_keys;
                this.$emit('onSelectChange', selected_row_keys);
            }
        },
        onActionButtons(event, type) {
            switch (event.key) {
                case 'export':
                    this.onExport();
                    break;
            }
        },
    },
};
</script>
